import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Factory, Settings, Wrench, Package, Sparkles, Zap, Award, Clock, Target } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { mockData } from '../mockData';

export const Home = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const services = [
    { icon: Factory, title: t.services.conveyor.title, desc: t.services.conveyor.description, gradient: 'from-cyan-500 to-blue-600' },
    { icon: Settings, title: t.services.helical.title, desc: t.services.helical.description, gradient: 'from-blue-600 to-indigo-600' },
    { icon: Package, title: t.services.chocolate.title, desc: t.services.chocolate.description, gradient: 'from-indigo-600 to-purple-600' },
    { icon: Wrench, title: t.services.cnc.title, desc: t.services.cnc.description, gradient: 'from-purple-600 to-pink-600' }
  ];

  const whyUs = [
    { icon: Clock, title: t.whyUs.experience.title, desc: t.whyUs.experience.description, gradient: 'from-cyan-500 to-cyan-600' },
    { icon: Award, title: t.whyUs.quality.title, desc: t.whyUs.quality.description, gradient: 'from-blue-500 to-blue-600' },
    { icon: Zap, title: t.whyUs.delivery.title, desc: t.whyUs.delivery.description, gradient: 'from-indigo-500 to-indigo-600' },
    { icon: Target, title: t.whyUs.custom.title, desc: t.whyUs.custom.description, gradient: 'from-purple-500 to-purple-600' }
  ];

  return (
    <div className="min-h-screen overflow-hidden bg-gradient-to-b from-gray-50 to-white">
      {/* Ultra Modern Hero Section */}
      <section className="relative pt-32 pb-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-900 opacity-95"></div>
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-cyan-500 to-blue-500 animate-gradient"></div>
        </div>
        <div className="absolute inset-0 overflow-hidden">
          <div className="particle particle-1"></div>
          <div className="particle particle-2"></div>
          <div className="particle particle-3"></div>
          <div className="particle particle-4"></div>
          <div className="particle particle-5"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white space-y-8">
              <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20">
                <Sparkles className="mr-2" size={16} />
                <span className="text-sm font-medium">{language === 'tr' ? 'Endüstriyel Üretimde Lider' : 'Leader in Industrial Manufacturing'}</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
                <span className="bg-gradient-to-r from-white via-blue-100 to-cyan-200 bg-clip-text text-transparent">
                  {t.hero.title}
                </span>
              </h1>
              
              <p className="text-xl md:text-2xl text-blue-100 leading-relaxed">
                {t.hero.subtitle}
              </p>
              
              <Link
                to="/contact"
                className="group inline-flex items-center px-10 py-5 bg-white text-blue-700 font-bold rounded-2xl hover:bg-blue-50 transition-all duration-300 shadow-2xl hover:shadow-white/20 hover:scale-105"
              >
                {t.hero.cta}
                <ArrowRight className="ml-3 group-hover:translate-x-2 transition-transform" size={24} />
              </Link>
            </div>
            
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                <img
                  src="/images/machines/hero-conveyor.png"
                  alt="Industrial Machinery"
                  className="rounded-3xl w-full h-[500px] object-contain bg-white"
                />
              </div>
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-cyan-400 rounded-full blur-3xl opacity-50 animate-pulse"></div>
              <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-blue-500 rounded-full blur-3xl opacity-50 animate-pulse delay-1000"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Ultra Modern Services */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-sm font-semibold rounded-full mb-6 shadow-lg">
              {language === 'tr' ? 'Çözümlerimiz' : 'Our Solutions'}
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              {t.services.title}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">{t.services.subtitle}</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
            {services.map((service, index) => (
              <Link
                key={index}
                to={index === 0 ? "/services/conveyor" : index === 1 ? "/catalog" : index === 2 ? "/services/chocolate" : "/services/cnc"}
                className="group relative bg-white/50 backdrop-blur-sm rounded-3xl p-4 md:p-8 hover:bg-white transition-all duration-500 hover:-translate-y-4 hover:shadow-2xl border border-gray-100"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-10 rounded-3xl transition-opacity duration-500`}></div>
                
                <div className={`w-12 h-12 md:w-20 md:h-20 bg-gradient-to-br ${service.gradient} rounded-2xl flex items-center justify-center mb-3 md:mb-6 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-xl mx-auto md:mx-0`}>
                  <service.icon className="text-white" size={24} />
                </div>
                
                <h3 className="text-base md:text-2xl font-bold text-gray-900 mb-2 md:mb-4 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-cyan-600 group-hover:bg-clip-text transition-all duration-300 text-center md:text-left">
                  {service.title}
                </h3>
                
                <p className="text-gray-600 leading-relaxed mb-4 md:mb-6 hidden md:block">
                  {service.desc}
                </p>
                
                <div className="flex items-center justify-center md:justify-start text-blue-600 font-semibold text-sm group-hover:translate-x-2 transition-transform">
                  <span>{language === 'tr' ? 'Detaylar' : 'Details'}</span>
                  <ArrowRight className="ml-2" size={16} />
                </div>
              </Link>
            ))}
          </div>
          
          <div className="text-center mt-16">
            <Link
              to="/catalog"
              className="inline-flex items-center px-12 py-6 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold text-lg rounded-2xl hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 shadow-2xl hover:shadow-blue-500/50 hover:scale-105"
            >
              {language === 'tr' ? 'Tüm Hizmetleri Gör' : 'View All Services'}
              <ArrowRight className="ml-3" size={24} />
            </Link>
          </div>
        </div>
      </section>

      {/* Ultra Modern Why Us */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden bg-gradient-to-br from-blue-50 via-cyan-50 to-indigo-50">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-400 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-400 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              {t.whyUs.title}
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {whyUs.map((item, index) => (
              <div key={index} className="group relative bg-white/70 backdrop-blur-md rounded-3xl p-6 lg:p-8 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-white">
                <div className={`w-16 lg:w-20 h-16 lg:h-20 bg-gradient-to-br ${item.gradient} rounded-2xl flex items-center justify-center mx-auto mb-4 lg:mb-6 transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 shadow-lg`}>
                  <item.icon className="text-white" size={32} />
                </div>
                
                <h3 className="text-lg lg:text-2xl font-bold text-gray-900 mb-2 lg:mb-3 text-center">
                  {item.title}
                </h3>
                
                <p className="text-gray-600 text-center leading-relaxed text-sm lg:text-base">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* References */}
      <section className="py-20 bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t.references.title}
            </h2>
            <p className="text-lg text-gray-600">{t.references.subtitle}</p>
          </div>
          
          <div className="relative overflow-hidden">
            <div className="flex animate-scroll space-x-4 md:space-x-12 items-center">
              {mockData.references.map((ref, index) => (
                <div
                  key={`ref-1-${index}`}
                  className="flex-shrink-0 bg-white p-3 md:p-6 rounded-lg shadow-sm hover:shadow-lg transition-shadow flex items-center justify-center min-w-[110px] md:min-w-[200px] h-[70px] md:h-[120px]"
                >
                  <img 
                    src={ref.logo} 
                    alt={ref.name}
                    className="max-w-[100px] md:max-w-[180px] max-h-[50px] md:max-h-[100px] w-auto h-auto object-contain"
                  />
                </div>
              ))}
              {mockData.references.map((ref, index) => (
                <div
                  key={`ref-2-${index}`}
                  className="flex-shrink-0 bg-white p-3 md:p-6 rounded-lg shadow-sm hover:shadow-lg transition-shadow flex items-center justify-center min-w-[110px] md:min-w-[200px] h-[70px] md:h-[120px]"
                >
                  <img 
                    src={ref.logo} 
                    alt={ref.name}
                    className="max-w-[100px] md:max-w-[180px] max-h-[50px] md:max-h-[100px] w-auto h-auto object-contain"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Ultra Modern CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-cyan-600 to-indigo-700"></div>
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-cyan-500 to-blue-500 animate-gradient"></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8">
            {language === 'tr'
              ? 'Projeniz için özel çözüm arıyor musunuz?'
              : 'Looking for a custom solution for your project?'}
          </h2>
          <p className="text-xl md:text-2xl text-cyan-100 mb-12">
            {language === 'tr'
              ? 'Uzman ekibimizle iletişime geçin, size özel teklif hazırlayalım.'
              : 'Contact our expert team and let us prepare a custom quote for you.'}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-12 py-6 bg-white text-blue-700 font-bold text-lg rounded-2xl hover:bg-blue-50 transition-all duration-300 shadow-2xl hover:shadow-white/20 hover:scale-105"
          >
            {t.nav.contact}
            <ArrowRight className="ml-3" size={24} />
          </Link>
        </div>
      </section>
    </div>
  );
};
